import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { login, loginEx } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';

const FLAGS: Record<string, string> = { UA: '🇺🇦', PL: '🇵🇱', EN: '🇬🇧' };

export default function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();

  const [loginInput, setLoginInput] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!loginInput.trim() || !password) {
      setError(t('login_error_empty'));
      return;
    }
    setLoading(true);
    // login() серверний і асинхронний — обовʼязково await, інакше user буде
    // Promise (завжди truthy) і навігація спрацює до отримання токена.
    try {
      const r = await loginEx(loginInput, password);
      if ('user' in r) {
        navigate('/');
      } else if (r.error.kind === 'rate_limited') {
        // Ліміт спроб — пароль тут ні до чого, і підказка має це казати.
        setError(t('login_error_rate', { sec: r.error.retryAfterSec }));
      } else if (r.error.kind === 'unavailable') {
        setError(t('login_error_unavailable'));
      } else {
        setError(t('login_error_wrong'));
        setPassword('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden flex flex-col items-center justify-center">
      {/* Background image — aerial solar farm */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(/login-bg.webp)',
        }}
      />

      {/* Dark overlay for readability */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.18) 100%)',
        }}
      />

      {/* Language switch — top-right corner */}
      <div className="absolute top-5 right-5 z-20 flex items-center gap-2">
        {(['UA', 'PL', 'EN'] as const).map((lang, idx) => (
          <span key={lang} className="flex items-center gap-2">
            {idx > 0 && <span className="text-[10px] text-white/30">·</span>}
            <button
              type="button"
              onClick={() => setLanguage(lang)}
              className="flex items-center gap-1 cursor-pointer transition-opacity"
              style={{ opacity: language === lang ? 1 : 0.45 }}
            >
              <span className="text-sm">{FLAGS[lang]}</span>
              <span
                className="text-[11px] font-semibold"
                style={{ color: language === lang ? '#fff' : 'rgba(255,255,255,0.5)' }}
              >
                {lang}
              </span>
            </button>
          </span>
        ))}
      </div>

      {/* ─── LOGIN CARD ─── */}
      <div
        className="relative z-10 w-full max-w-[400px] mx-4 rounded-3xl px-10 py-10 backdrop-blur-md"
        style={{
          background: 'rgba(42, 42, 45, 0.72)',
          border: '1px solid rgba(255, 255, 255, 0.16)',
        }}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="https://storage.helloreaddy.io/project_files/f4fc278b-9708-4307-b2ca-b98fe5fad037/e26caf0b-5a4a-4665-a8ee-257daf70e79a_compressed_Tepla_-Helios_AG_white.webp"
            alt="Helios"
            className="mb-2 object-contain"
            style={{ height: '60px' }}
          />
          <p
            className="text-[10px] font-semibold tracking-[0.22em] uppercase"
            style={{ color: '#8B8B94' }}
          >
            HELIOS ENERGY MANAGEMENT SYSTEM
          </p>
        </div>

        {/* Sign in heading */}
        <h2
          className="text-center font-semibold mb-7"
          style={{ color: '#fff', fontSize: '18px', letterSpacing: '-0.01em' }}
        >
          Увійти у ваш робочий простір
        </h2>

        {/* Email */}
        <div className="mb-4">
          <label
            className="block text-xs font-medium mb-1.5"
            style={{ color: '#B0B0B8' }}
          >
            Логін
          </label>
          <input
            type="text"
            value={loginInput}
            onChange={(e) => { setLoginInput(e.target.value); setError(''); }}
            onKeyDown={handleKeyDown}
            placeholder="Введіть email"
            className="w-full px-3.5 py-2.5 rounded-lg text-sm outline-none transition-all"
            style={{
              background: '#222225',
              border: `1px solid ${error ? 'var(--accent)' : '#3A3A3D'}`,
              color: '#fff',
            }}
            autoComplete="username"
          />
        </div>

        {/* Password + Forgot */}
        <div className="mb-2">
          <div className="flex items-center justify-between mb-1.5">
            <label
              className="text-xs font-medium"
              style={{ color: '#B0B0B8' }}
            >
              Пароль
            </label>
            <button
              type="button"
              className="text-[11px] cursor-pointer hover:opacity-75 transition-opacity"
              style={{ color: '#8B8B94', textDecoration: 'underline' }}
            >
              Забули пароль?
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              onKeyDown={handleKeyDown}
              placeholder="Введіть пароль"
              className="w-full px-3.5 pr-10 py-2.5 rounded-lg text-sm outline-none transition-all"
              style={{
                background: '#222225',
                border: `1px solid ${error ? 'var(--accent)' : '#3A3A3D'}`,
                color: '#fff',
              }}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer w-5 h-5 flex items-center justify-center"
            >
              <i
                className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}
                style={{ color: '#6B6B74' }}
              />
            </button>
          </div>
        </div>

        {/* Error */}
        <div className="mb-4 h-5">
          {error && (
            <div className="flex items-center gap-1.5">
              <i className="ri-error-warning-line text-xs" style={{ color: 'var(--accent)' }} />
              <p className="text-xs font-medium" style={{ color: 'var(--accent)' }}>{error}</p>
            </div>
          )}
        </div>

        {/* Sign in button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="w-full py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-all whitespace-nowrap flex items-center justify-center gap-2 mb-5"
          style={{
            background: loading ? '#4A4A4E' : '#5A5A5E',
            color: '#fff',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#6B6B74'; }}
          onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#5A5A5E'; }}
        >
          {loading
            ? <><i className="ri-loader-4-line animate-spin text-base" /> {t('login_checking')}</>
            : <>Увійти</>
          }
        </button>

        {/* Sign up link */}
        <p className="text-center text-xs" style={{ color: '#8B8B94' }}>
          Ще не маєте акаунту?{' '}
          <span style={{ color: '#fff', textDecoration: 'underline' }}>
            Реєстрація
          </span>
        </p>
      </div>

      {/* ─── BOTTOM TAGLINE ─── */}
      <div className="relative z-10 mt-8 text-center px-4">
        <h3
          className="font-semibold mb-1"
          style={{ color: '#fff', fontSize: '22px', letterSpacing: '-0.01em' }}
        >
          Ласкаво просимо до Helios EMS
        </h3>
      </div>
    </div>
  );
}